import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
	createSession,
	startSession,
	sendIceCandidate,
	endSession,
	sendText,
} from "../../services/sessionService";
import styles from "./ChatInterface.module.css";
import { AuthContext } from "../../shared/context/auth-context";
import { useHttpClient } from "../../shared/hooks/http-hook";
import { Spinner } from "react-bootstrap";
import ToastError from "../../shared/elements/ToastError";

const ChatInterface = ({ onBack, knowledgeBases, selectedAvatar }) => {
	const [chatStarted, setChatStarted] = useState(false);
	const [sessionKnowledgeBase, setSessionKnowledgeBase] = useState(null);

	const [sessionId, setSessionId] = useState(null);
	const [pc, setPc] = useState(null);
	const [isStartingUp, setIsStartingUp] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const videoRef = useRef(null);
	const auth = useContext(AuthContext);
	const { sendRequest } = useHttpClient();
	const [textToSend, setTextToSend] = useState("");
	const [isSending, setIsSending] = useState(false);
	const [assistantId, setAssistantId] = useState(null);
	const [threadId, setThreadId] = useState(null);

	useEffect(() => {
		return () => {
			if (pc) {
				pc.close();
			}
		};
	}, [pc]);

	const kbById = useMemo(
		() => Object.fromEntries(knowledgeBases.map((kb) => [kb.id, kb])),
		[knowledgeBases]
	);
	const selectedKb = kbById[sessionKnowledgeBase] ?? null;

	const placeholderLabel = selectedKb?.name ?? "Select a knowledge base";

	useEffect(() => {
		setAssistantId(selectedKb?.assistantId || null);
	}, [selectedKb]);

	// Handle knowledge base change
	const handleKnowledgeBaseChange = async (e) => {
		setSessionKnowledgeBase(e.target.value);
	};

	const textHandler = async () => {
		if (!textToSend.trim() || !assistantId || !threadId) {
			setErrorMessage("Missing required fields.");
			return;
		}

		try {
			await sendText(
				sessionId,
				textToSend,
				assistantId,
				threadId,
				auth.token
			);
			/*setChatHistory((prev) => [
				...prev,
				{ text: textToSend, sender: "user" },
				{ text: response, sender: "agent" },
			]);*/
			setTextToSend("");
		} catch (error) {
			console.error("Error sending text:", error);
			setErrorMessage("Error sending text: " + error.message);
		}
	};

	const handleInputChange = (e) => setTextToSend(e.target.value);

	const handleKeyDown = (e) => {
		if (isSending) return;
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			sendPrompt();
		}
	};

	const sendPrompt = async () => {
		if (isSending || !pc || !sessionId || !textToSend.trim()) return;
		setIsSending(true);
		try {
			await textHandler();
		} catch (error) {
			console.error("Error sending prompt:", error);
			setErrorMessage("Error sending prompt: " + error.message);
		} finally {
			setIsSending(false);
		}
	};

	const startSessionHandler = async () => {
		if (!sessionKnowledgeBase) {
			setErrorMessage("Please select a knowledge base.");
			return;
		};
		try {
			// Create a new Peer Connection with ICE servers from HeyGen
			setIsStartingUp(true);
			const session = await createSession(auth.token, sendRequest);
			const peerConnection = new RTCPeerConnection({
				iceServers: session.ice_servers,
			});
			setPc(peerConnection);

			// Set the remote video stream when received
			peerConnection.ontrack = (event) => {
				if (videoRef.current) {
					videoRef.current.srcObject = event.streams[0];
				}
			};

			// Ensure type and sdp are not null
			const { type, sdp } = session;
			if (type && sdp) {
				await peerConnection.setRemoteDescription(
					new RTCSessionDescription({ type, sdp })
				);
			} else {
				throw new Error("Invalid type or sdp");
			}

			peerConnection.onicecandidate = async (event) => {
				if (event.candidate) {
					try {
						await sendIceCandidate(
							session.session_id,
							event.candidate,
							auth.token
						);
					} catch (error) {
						console.error("Error sending ICE candidate:", error);
						setErrorMessage("Error sending ICE candidate: " + error.message);
					}
				}
			};

			const answer = await peerConnection.createAnswer();
			await peerConnection.setLocalDescription(answer);

			// Start session with the local SDP answer
			const sessionResponse = await startSession(session.session_id, answer, auth.token, sendRequest);
			setThreadId(sessionResponse.threadId);
			setSessionId(session.session_id);
			setIsStartingUp(false);
			setChatStarted(true);
		} catch (error) {
			setIsStartingUp(false);
			console.error("Failed to start session:", error);
			const errorMessage =
				error instanceof Error ? error.message : JSON.stringify(error);
			console.log("Failed to start session: " + errorMessage);
			setErrorMessage("Failed to start session: " + errorMessage);
		}
	};

	const endSessionHandler = async () => {
		try {
			await cleanup(true);
		} catch (error) {
			console.error("Failed to end session:", error);
			const errorMessage =
				error instanceof Error ? error.message : JSON.stringify(error);
			setErrorMessage("Failed to end session: " + errorMessage);
		}
	};

	const cleanup = async (manualCleanup = false) => {
		if (pc) {
			pc.close();
			setPc(null);
		}
		if (sessionId) {
			try {
				await endSession(sessionId, auth.token);
				setSessionId(null);
			} catch (error) {
				console.error("Error ending HeyGen session:", error);
			}
		}
		if (manualCleanup) {
			setErrorMessage(null);
		}
	};

	useEffect(() => {
		const textarea = document.querySelector(`.${styles.messageInput}`);
		if (textarea) {
			textarea.style.height = "auto";
			textarea.style.height = `${textarea.scrollHeight}px`;
		}
	}, [textToSend]);

	const handleOnBack = () => {
		if (pc) {
			pc.close();
			setPc(null);
		}
		if (sessionId) {
			try {
				endSession(sessionId, auth.token);
			} finally {
				setSessionId(null);
			}
		}
		onBack();
	};


	if (!selectedAvatar) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<button className={styles.backButton} onClick={onBack}>
						← Back
					</button>
				</div>
				<div className={styles.emptyState}>
					<div className={styles.emptyStateIcon}>?</div>
					<p>
						No avatar selected. Please go back and select an avatar to chat
						with.
					</p>
					<button
						className={`${styles.button} ${styles.saveButton}`}
						onClick={onBack}
					>
						Select an Avatar
					</button>
				</div>
			</div>
		);
	}

	// Always render the full container, but update the avatar display when chat is started
	return (
		<div className={styles.fullContainer}>
			{/* Error message display */}
			<ToastError
				show={!!errorMessage}
				onClose={() => setErrorMessage(null)}
				message={errorMessage}
			/>
			{/* Header with back button and avatar name */}
			<div className={styles.fullHeader}>
				<button className={styles.backButton} onClick={handleOnBack}>
					← Chat with {selectedAvatar.avatar_name}
				</button>
				<div className={styles.headerActions}>
					<button className={styles.actionButton}>
						<span className={styles.buttonIcon}>&lt;/&gt;</span>
						Embed
					</button>
					<button className={styles.actionButton}>
						<span className={styles.buttonIcon}>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<rect
									x="4"
									y="4"
									width="12"
									height="12"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M16 12V16C16 17.1046 15.1046 18 14 18H8C6.89543 18 6 17.1046 6 16V10C6 8.89543 6.89543 8 8 8H12"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M11 13L20 4"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
								<path
									d="M15 4H20V9"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
						Share
					</button>
				</div>
			</div>

			{/* Main avatar display - changes based on chatStarted state */}
			<div className={styles.avatarDisplay}>
				<div className={styles.mediaFrame}>
					{pc ? (
						<video
							ref={videoRef}
							className={styles.mediaContent}
							autoPlay
							playsInline
						/>
					) : (
						/* Before chat starts, show the static avatar image */
						<img
							src={selectedAvatar.image_url}
							alt={selectedAvatar.avatar_name}
							className={styles.mediaContent}
						/>
					)}
				</div>
			</div>

			{/* Bottom controls - change button text based on chat state */}
			<div className={styles.bottomControls}>
				{!pc ? ( //switch back to !pc after style changes
					// if isStartingUp, show loading indicator
					<>
						{isStartingUp ? (
							<div className={styles.languageLabel}>
								<Spinner animation="border" size="sm" /> Starting session...
							</div>
						) : (
							<>
								<div className={styles.languageBox}>
									<span className={styles.languageLabel}>English</span>
								</div>
								<div className={styles.knowledgeSelector}>
									<select
										className={styles.languageSelect}
										value={sessionKnowledgeBase || ""}
										onChange={handleKnowledgeBaseChange}
										disabled={chatStarted}
									>
										<option value="" disabled hidden>
											{placeholderLabel}
										</option>
										{knowledgeBases.map((kb) => (
											<option key={kb.id} value={kb.id}>
												{kb.name}
											</option>
										))}
									</select>
								</div>
								<button
									className={styles.chatNowButton}
									onClick={startSessionHandler}
								>
									Chat now
								</button>
							</>
						)}
					</>
				) : (
					<>
						<div className={styles.messagePill}>
							<textarea
								className={styles.messageInput}
								value={textToSend}
								onChange={handleInputChange}
								onKeyDown={handleKeyDown}
								placeholder="Type your message..."
								rows="1"
								disabled={isSending}
							/>
							<button
								className={styles.sendButton}
								onClick={sendPrompt}
								disabled={isSending || !textToSend.trim()}
							>
								{isSending ? <Spinner animation="border" size="sm" /> : "Send"}
							</button>
						</div>
						<button
							className={styles.chatNowButton}
							onClick={endSessionHandler}
						>
							End Session
						</button>
					</>
				)}
			</div>
		</div>
	);
};

export default ChatInterface;

/*
<div className={styles.avatarDisplayInner}>
						<img
							src={selectedAvatar.image_url}
							alt={`${selectedAvatar.avatar_name} (Interactive)`}
							className={styles.avatarFullImage}
						/>
						<div
							style={{
								position: "absolute",
								bottom: "20px",
								left: "0",
								right: "0",
								textAlign: "center",
								padding: "10px",
								backgroundColor: "rgba(0,0,0,0.5)",
								color: "white",
								fontSize: "14px",
							}}
						>
							Interactive Avatar Ready
						</div>
					</div>
          */
